/**
 * Settings, Policies: "When nobody answers" (R-HL-5). The org default and per-project overrides
 * of how long a request waits, what happens then, the hold cap and the reminder, with a preview
 * of exactly what the agent is told. Owners and admins edit; everyone else reads.
 */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Hourglass, Pencil, Plus, Trash2 } from '@orgus/ui/icons';
import { useShallow } from 'zustand/react/shallow';
import { expiryPolicyFor } from '@orgus/protocol';
import type { ApprovalExpiryPolicy, ApprovalExpirySettings, Id, OnExpiry } from '@orgus/protocol';
import { Badge, Button, EmptyState, ErrorState, IconButton, Input, Panel, REVEAL_GROUP, RelativeTime, RevealSlot, Segmented, Select, Skeleton, cn, toUiError, toast } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { ON_EXPIRY_LABEL, OLD_AGENT_LIMIT_MINUTES, TERMINAL_PREVIEW_NOTE, formOfPolicy, overrideRows, policyOfForm, previewText, saveExpiry, settingsWith, summaryOf, useExpirySettings, withoutProject } from './expiryModel.ts';
import type { PolicyForm } from './expiryModel.ts';
import { ModuleAbsent } from '../server/ModuleAbsent.tsx';
import { useModuleAbsent } from '../server/server.ts';

type Editing = { key: 'org' } | { key: 'project'; projectId: Id | null; isNew: boolean };

function PolicyEditor({
  settings,
  editing,
  projects,
  onDone,
}: {
  settings: ApprovalExpirySettings;
  editing: Editing;
  projects: { id: Id; name: string }[];
  onDone: () => void;
}) {
  const start = editing.key === 'org' ? settings.org : expiryPolicyFor(settings, editing.projectId);
  const [form, setForm] = useState<PolicyForm>(() => formOfPolicy(start));
  const [projectId, setProjectId] = useState<Id | null>(editing.key === 'project' ? editing.projectId : null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const parsed = policyOfForm(form);
  const set = <K extends keyof PolicyForm>(k: K, v: PolicyForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setSaveError(null);
  };
  const needsProject = editing.key === 'project' && !projectId;

  const save = async () => {
    if (!parsed.ok || needsProject) return;
    setBusy(true);
    setSaveError(null);
    try {
      await saveExpiry(settingsWith(settings, editing.key === 'org' ? null : projectId, parsed.policy));
      toast('Expiry setting saved', { tone: 'success', description: 'It applies to requests raised from now on.' });
      onDone();
    } catch (e) {
      const u = toUiError(e);
      setSaveError(`${u.what} ${u.why ?? ''} ${u.next ?? ''}`.replace(/\s+/g, ' ').trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4 rounded-card bg-raised px-4 py-4"
      aria-label={editing.key === 'org' ? 'Edit the default' : 'Edit a project override'}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      {editing.key === 'project' && editing.isNew && (
        <Select label="Project" value={projectId ?? ''} placeholder="Pick a project" onValueChange={(v) => setProjectId(v || null)} options={projects.map((p) => ({ value: p.id, label: p.name }))} />
      )}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-fg-muted">When nobody answers</span>
        <Segmented<OnExpiry>
          label="When nobody answers"
          value={form.onExpiry}
          onChange={(v) => set('onExpiry', v)}
          options={[
            { value: 'deny', label: ON_EXPIRY_LABEL.deny },
            { value: 'hold', label: ON_EXPIRY_LABEL.hold },
          ]}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          label={form.onExpiry === 'hold' ? 'Escalate after (minutes)' : 'Deny after (minutes)'}
          inputMode="numeric"
          value={form.timeoutMinutes}
          onChange={(e) => set('timeoutMinutes', e.target.value)}
          hint="1 to 240"
          className="num"
        />
        {form.onExpiry === 'hold' && (
          <Input label="Deny at (minutes)" inputMode="numeric" value={form.holdMaxMinutes} onChange={(e) => set('holdMaxMinutes', e.target.value)} hint="The longest wait, up to 720" className="num" />
        )}
        <Input label="Remind before (minutes)" inputMode="numeric" value={form.remindBeforeMinutes} onChange={(e) => set('remindBeforeMinutes', e.target.value)} hint="0 for no reminder" className="num" />
      </div>
      {form.onExpiry === 'hold' && (
        <p className="text-sm text-fg-muted">
          The session keeps waiting and its mission shows as waiting for you. Machines with an older agent give up after {OLD_AGENT_LIMIT_MINUTES} minutes, so there the request is
          denied then, and the agent is told so.
        </p>
      )}
      {!parsed.ok ? (
        <p role="alert" className="rounded-control bg-failed-soft px-3 py-2 text-sm text-failed">
          {parsed.error}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg-muted">What the agent is told</span>
          <p className="rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-fg">{previewText(parsed.policy)}</p>
          <p className="text-sm text-fg-subtle">{TERMINAL_PREVIEW_NOTE}</p>
        </div>
      )}
      {saveError && (
        <p role="alert" className="rounded-control bg-failed-soft px-3 py-2 text-sm text-failed">
          {saveError}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={busy} disabled={!parsed.ok || needsProject}>
          Save
        </Button>
      </div>
    </form>
  );
}

/**
 * One expiry setting. On a desktop: the name and its sentence, the outcome pill, then the row
 * actions revealed on hover or focus. Below 768 px it stacks: the name and actions on line one,
 * the pill on line two, the sentence at full width under it, so nothing wraps a word a line.
 */
function Row({ title, sub, p, badge, actions }: { title: string; sub?: string; p: ApprovalExpiryPolicy; badge?: string; actions?: ReactNode }) {
  const pill = (
    <Badge tone={p.onExpiry === 'hold' ? 'waiting' : 'failed'} icon={<Hourglass />} className="shrink-0">
      {ON_EXPIRY_LABEL[p.onExpiry]}
    </Badge>
  );
  const slot = actions ? <RevealSlot className="flex w-14 shrink-0 items-center justify-end gap-1">{actions}</RevealSlot> : null;
  return (
    <div data-row="" className={cn(REVEAL_GROUP, 'flex items-center gap-3 rounded-control px-4 py-2 transition-colors duration-(--duration-fast) hover:bg-hover')}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex min-w-0 items-center gap-2 text-ui font-medium text-fg">
          <span className="truncate">{title}</span>
          {badge && <Badge tone="neutral" variant="outline" className="shrink-0">{badge}</Badge>}
          {slot && <span className="ml-auto md:hidden">{slot}</span>}
        </span>
        <span className="md:hidden">{pill}</span>
        <span className="text-caption text-fg-muted">{summaryOf(p)}</span>
        {sub && <span className="text-caption text-fg-subtle">{sub}</span>}
      </div>
      <span className="hidden md:inline-flex">{pill}</span>
      {slot && <span className="hidden md:inline-flex">{slot}</span>}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="flex flex-col gap-2 p-4" role="status" aria-label="Loading the expiry setting">
      <Skeleton className="h-5 w-56" />
      <Skeleton className="h-4 w-80" />
      <Skeleton className="h-5 w-48" />
    </div>
  );
}

export function ExpiryPolicyPanel() {
  const { phase, settings, error, mock, retry } = useExpirySettings();
  const absent = useModuleAbsent('/api/approvals/expiry');
  const canEdit = useCan('admin');
  const narrow = useMediaQuery(NARROW_QUERY);
  const projectMap = useOrgus(useShallow((s) => s.projects));
  const projects = useMemo(() => Object.values(projectMap).map((p) => ({ id: p.id, name: p.name })).sort((a, b) => a.name.localeCompare(b.name)), [projectMap]);
  const name = (id: Id) => projectMap[id]?.name;
  const [editing, setEditing] = useState<Editing | null>(null);
  const [removing, setRemoving] = useState<Id | null>(null);

  const remove = async (projectId: Id) => {
    if (!settings) return;
    setRemoving(projectId);
    try {
      await saveExpiry(withoutProject(settings, projectId));
      toast('Override removed', { tone: 'neutral', description: `${name(projectId) ?? 'The project'} uses the default from now on.` });
    } catch (e) {
      const u = toUiError(e);
      toast(`Could not remove the override: ${u.what}`, { tone: 'error', description: u.next });
    } finally {
      setRemoving(null);
    }
  };

  const rows = settings ? overrideRows(settings, name) : [];
  const free = projects.filter((p) => !settings?.projects[p.id]);

  return (
    <Panel
      title="When nobody answers"
      sub={narrow ? undefined : "applies to requests raised after a change"}
      icon={<Hourglass />}
      flush
      actions={
        <div className="flex items-center gap-2">
          {mock && !narrow && (
            <Badge tone="neutral" variant="outline">
              Demo data
            </Badge>
          )}
          {canEdit && settings &&
            (narrow ? (
              <IconButton size="sm" label="Add a project override" icon={<Plus />} disabled={!free.length} onClick={() => setEditing({ key: 'project', projectId: null, isNew: true })} />
            ) : (
              <Button size="sm" icon={<Plus />} disabled={!free.length} title={free.length ? undefined : 'Every project has an override.'} onClick={() => setEditing({ key: 'project', projectId: null, isNew: true })}>
                Add a project override
              </Button>
            ))}
        </div>
      }
    >
      {phase === 'loading' ? (
        <PanelSkeleton />
      ) : phase === 'missing' || absent ? (
        <ModuleAbsent
          icon={<Hourglass />}
          feature="Approval expiry settings"
          plural
          hub={{ title: 'Expiry settings are not available on this hub', description: 'Restart the hub on the current Orgus version to change the 15-minute expiry.' }}
          instead="Until then, an unanswered request expires after 15 minutes."
        />
      ) : phase === 'error' || !settings ? (
        <ErrorState error={error ?? { what: 'Could not load the expiry setting.', why: 'The hub did not answer.', next: 'Try again.' }} onRetry={retry} />
      ) : (
        <div className="flex flex-col">
          <div className={cn(editing?.key === 'org' && 'bg-raised')}>
            <Row
              title="Every project"
              badge="Default"
              p={settings.org}
              actions={canEdit && <IconButton size="xs" label="Edit the default" icon={<Pencil />} onClick={() => setEditing({ key: 'org' })} />}
            />
            {editing?.key === 'org' && <PolicyEditor settings={settings} editing={editing} projects={projects} onDone={() => setEditing(null)} />}
          </div>
          {rows.length === 0 && !(editing?.key === 'project' && editing.isNew) ? (
            <p className="px-4 py-3 text-caption text-fg-subtle">Every project uses the default.</p>
          ) : (
            rows.map((r) => (
              <div key={r.projectId}>
                <Row
                  title={r.name}
                  badge="Override"
                  p={r.policy}
                  actions={
                    canEdit && (
                      <>
                        <IconButton size="xs" label={`Edit the override for ${r.name}`} icon={<Pencil />} onClick={() => setEditing({ key: 'project', projectId: r.projectId, isNew: false })} />
                        <IconButton size="xs" label={`Remove the override for ${r.name}`} icon={<Trash2 />} variant="danger" loading={removing === r.projectId} onClick={() => void remove(r.projectId)} />
                      </>
                    )
                  }
                />
                {editing?.key === 'project' && editing.projectId === r.projectId && !editing.isNew && (
                  <PolicyEditor settings={settings} editing={editing} projects={projects} onDone={() => setEditing(null)} />
                )}
              </div>
            ))
          )}
          {editing?.key === 'project' && editing.isNew && (
            <div>
              <PolicyEditor settings={settings} editing={editing} projects={free} onDone={() => setEditing(null)} />
            </div>
          )}
          <p className="px-4 pb-2 pt-3 text-caption text-fg-subtle">
            {canEdit ? 'Owners and admins change this.' : 'Only an owner or admin can change this.'}
            {settings.updatedAt ? (
              <>
                {' '}
                Last changed <RelativeTime ts={settings.updatedAt} />
                {settings.updatedBy ? ` by ${settings.updatedBy}` : ''}.
              </>
            ) : null}
          </p>
        </div>
      )}
    </Panel>
  );
}
